import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../shared/store/auth.store.js'
import {
  createCity,
  deleteCity,
  getCities,
  updateCity,
  type CityRef,
} from '../../shared/api/cities.js'
import { ApiError } from '../../shared/types/api.js'
import SidePanel from '../../shared/ui/SidePanel.js'
import StatusDot from '../../shared/ui/StatusDot.js'
import { GearIcon, TrashIcon } from '../../shared/ui/icons.js'
import IconButton from '../../shared/ui/IconButton.js'
import { isPlatformAdminRole } from '../../shared/utils/permissions.js'
import { usePlatformApiError } from '../../app/usePlatformApiError.js'

type LoadStatus = 'loading' | 'ready' | 'error' | 'forbidden'

const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

interface PanelState {
  mode: 'create' | 'edit'
  city?: CityRef
}

export default function CitiesPage() {
  const { t } = useTranslation()
  const handleApiError = usePlatformApiError()
  const user = useAuthStore(s => s.user)
  const canManage = isPlatformAdminRole(user?.platformRole)

  const [cities, setCities] = useState<CityRef[]>([])
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [panel, setPanel] = useState<PanelState | null>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [cityStatus, setCityStatus] = useState('ACTIVE')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      const list = await getCities()
      setCities(list)
      setStatus('ready')
    } catch (err: unknown) {
      const kind = handleApiError(err)
      if (kind === null) return
      setStatus(kind === 'forbidden' ? 'forbidden' : 'error')
    }
  }, [handleApiError])

  useEffect(() => {
    void load()
  }, [load])

  function openPanel(next: PanelState) {
    setErrors({})
    setSuccess('')
    setName(next.city?.name ?? '')
    setSlug(next.city?.slug ?? '')
    setCityStatus(next.city?.status ?? 'ACTIVE')
    setPanel(next)
  }

  function closePanel() {
    setPanel(null)
    setErrors({})
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (saving) return

    const fieldErrors: Record<string, string> = {}
    if (!name.trim()) fieldErrors.name = t('error.fieldRequired')
    if (slug.trim() && !slugRegex.test(slug.trim())) {
      fieldErrors.slug = t('cities.slugPattern')
    }
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    setSaving(true)
    try {
      if (panel?.mode === 'edit' && panel.city) {
        const payload: { name: string; status: string; slug?: string } = {
          name: name.trim(),
          status: cityStatus,
        }
        const trimmedSlug = slug.trim()
        if (trimmedSlug && trimmedSlug !== panel.city.slug) payload.slug = trimmedSlug
        const updated = await updateCity(panel.city.id, payload)
        setCities(prev => prev.map(c => (c.id === updated.id ? updated : c)))
        setSuccess(t('cities.updated'))
      } else {
        const created = await createCity({
          name: name.trim(),
          slug: slug.trim() || undefined,
        })
        setCities(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
        setSuccess(t('cities.created'))
      }
      closePanel()
    } catch (err: unknown) {
      const kind = handleApiError(err)
      if (kind === null) return
      if (err instanceof ApiError && err.status === 409) {
        setErrors({ slug: t('cities.slugTaken') })
      } else if (kind === 'forbidden') {
        setErrors({ general: t('cities.forbidden') })
      } else if (kind === 'notFound') {
        setErrors({ general: t('platform.errors.notFound') })
      } else {
        setErrors({ general: t('cities.saveError') })
      }
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(city: CityRef) {
    if (deletingId) return
    if (!window.confirm(t('cities.deleteConfirm', { name: city.name }))) return

    setDeletingId(city.id)
    setSuccess('')
    setErrors({})
    try {
      await deleteCity(city.id)
      setCities(prev => prev.filter(c => c.id !== city.id))
      setSuccess(t('cities.deleted'))
    } catch (err: unknown) {
      const kind = handleApiError(err)
      if (kind === null) return
      if (err instanceof ApiError && err.code === 'CITY_HAS_LOCATIONS') {
        setErrors({ general: t('cities.deleteBlocked') })
      } else if (kind === 'forbidden') {
        setErrors({ general: t('cities.forbidden') })
      } else if (kind === 'notFound') {
        setErrors({ general: t('platform.errors.notFound') })
      } else {
        setErrors({ general: t('cities.deleteError') })
      }
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <section className="page-section">
      <div className="page-header">
        <div>
          <h1 className="platform-page-title">{t('platform.cities.title')}</h1>
          <p className="page-section-description">{t('cities.subtitle')}</p>
        </div>
        {canManage && (
          <button type="button" className="btn btn-primary" onClick={() => openPanel({ mode: 'create' })}>
            {t('cities.create')}
          </button>
        )}
      </div>

      {success && <div className="form-success-banner" role="status">{success}</div>}
      {errors.general && !panel && <div className="form-error-banner" role="alert">{errors.general}</div>}

      {status === 'loading' && (
        <div className="page-loading"><div className="spinner" /></div>
      )}

      {(status === 'error' || status === 'forbidden') && (
        <div className="settings-card">
          <div className="form-error-banner">
            {status === 'forbidden' ? t('cities.forbidden') : t('cities.loadError')}
          </div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            {t('error.retry')}
          </button>
        </div>
      )}

      {status === 'ready' && cities.length === 0 && (
        <div className="settings-card">
          <p className="page-section-description">{t('cities.empty')}</p>
        </div>
      )}

      {status === 'ready' && cities.length > 0 && (
        <div className="menu-grid">
          {cities.map(city => {
            const cityStatus = city.status === 'ACTIVE' || city.status === 'SUSPENDED' || city.status === 'ARCHIVED'
              ? city.status
              : 'ACTIVE'
            const statusLabel = t(
              cityStatus === 'ACTIVE'
                ? 'cities.statusActive'
                : cityStatus === 'SUSPENDED'
                  ? 'cities.statusSuspended'
                  : 'cities.statusArchived',
            )
            return (
              <div key={city.id} className="menu-card">
                <div className="menu-card-top">
                  <StatusDot status={cityStatus} label={statusLabel} />
                  <span className="menu-card-name">{city.name}</span>
                  {canManage && (
                    <div className="platform-card-actions">
                      <IconButton
                        label={t('cities.editCity', { name: city.name })}
                        onClick={() => openPanel({ mode: 'edit', city })}
                      >
                        <GearIcon />
                      </IconButton>
                      <IconButton
                        label={t('cities.deleteCity', { name: city.name })}
                        danger
                        disabled={deletingId === city.id}
                        onClick={() => void handleDelete(city)}
                      >
                        <TrashIcon />
                      </IconButton>
                    </div>
                  )}
                </div>
                {city.slug && <span className="menu-card-slug">{city.slug}</span>}
                <span className="field-hint">{statusLabel}</span>
              </div>
            )
          })}
        </div>
      )}

      <SidePanel
        open={panel !== null}
        onClose={closePanel}
        title={panel?.mode === 'edit' ? t('cities.editTitle') : t('cities.createTitle')}
      >
        <div>
          <form onSubmit={e => void handleSubmit(e)}>
            <div className="form-field">
              <label htmlFor="cityName">{t('cities.name')} *</label>
              <input
                id="cityName"
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setErrors({}) }}
                maxLength={200}
                required
                disabled={saving}
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="citySlug">{t('cities.slug')}</label>
              <input
                id="citySlug"
                type="text"
                value={slug}
                onChange={e => { setSlug(e.target.value); setErrors({}) }}
                maxLength={100}
                disabled={saving}
                autoComplete="off"
              />
              <span className="field-hint">{t('cities.slugAutoHint')}</span>
              {errors.slug && <span className="field-error">{errors.slug}</span>}
            </div>

            {panel?.mode === 'edit' && (
              <div className="form-field">
                <label htmlFor="cityStatus">{t('cities.status')}</label>
                <select
                  id="cityStatus"
                  value={cityStatus}
                  onChange={e => { setCityStatus(e.target.value); setErrors({}) }}
                  disabled={saving}
                >
                  <option value="ACTIVE">{t('cities.statusActive')}</option>
                  <option value="SUSPENDED">{t('cities.statusSuspended')}</option>
                  <option value="ARCHIVED">{t('cities.statusArchived')}</option>
                </select>
              </div>
            )}

            {errors.general && <div className="form-error-banner">{errors.general}</div>}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? t('cities.saving') : t('cities.save')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={closePanel} disabled={saving}>
                {t('menu.cancel')}
              </button>
            </div>
          </form>
        </div>
      </SidePanel>
    </section>
  )
}
